import { useEffect, useRef, useState } from "react";
import { api, type ItemSummary } from "./api";
import type { Scope } from "../components/ScopePicker";

interface Page { items: ItemSummary[]; total: number; limit: number; offset: number }

/** Rows per request. Search and the store filter run on the server, so the
 *  page only ever holds what is on screen plus what was scrolled past. */
const PAGE_SIZE = 60;

/**
 * The on-hand item list, paged from the server: first page whenever the scope
 * or a filter changes, the next page as `sentinel` scrolls into view.
 * Shared by the Inventory and Explorer tabs.
 */
export function useInventoryList(scope: Scope, needle: string, store: string | null) {
  const [items, setItems] = useState<ItemSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentinel = useRef<HTMLDivElement>(null);

  const listPath = (offset: number) =>
    `/items?status=draft,in_stock,listed&scope=${scope}&limit=${PAGE_SIZE}&offset=${offset}` +
    (needle ? `&q=${encodeURIComponent(needle)}` : "") +
    (store ? `&store=${encodeURIComponent(store)}` : "");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await api.get<Page>(listPath(0));
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load your inventory");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, needle, store]);

  const hasMore = items.length < total;

  async function loadMore() {
    if (loadingMore || loading || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await api.get<Page>(listPath(items.length));
      setItems((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        return [...prev, ...res.items.filter((i) => !seen.has(i.id))];
      });
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load more items");
    } finally {
      setLoadingMore(false);
    }
  }

  // Fetch the next page as the end of the list scrolls into view; the button
  // under the list stays as the fallback.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) void loadMore(); },
      { rootMargin: "600px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, items.length, loading, loadingMore]);

  return { items, total, loading, loadingMore, error, hasMore, loadMore, sentinel };
}
